import { useLazyLoadQuery, type PreloadedQuery } from 'react-relay';
import { type FC, type ComponentProps, useEffect } from 'react';
import {
    pdpRespMarketplacePrimaryRootQuery,
    type PdpRespMarketplacePrimaryRootQueryType,
    type pdpRespMarketplacePrimaryRootQuery$variables as PrimaryRelayVariables,
} from '../../../queries/pdpRespMarketplacePrimaryRoot.ts';
import { DblExternalContextProvider } from 'dibs-buyer-layout/exports/DblExternalContext.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { trackItemFavoritingFeb2026Test } from 'dibs-buyer-item-favoriting/src/abTests.ts';

// components
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500.tsx';
import { PdpRespMarketplaceLayoutPrimary } from './PdpRespMarketplaceLayoutPrimary.tsx';
import { PdpRespMarketplaceLayoutSecondaryRenderer } from './PdpRespMarketplaceLayoutSecondaryRenderer.tsx';

// types
import { type Store } from 'redux';
import { type Locale } from 'dibs-intl/exports/locales';
import {
    type pdpRespMarketplaceSecondaryRootQueryType,
    type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables,
} from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

type IntlProviderProps = ComponentProps<typeof IntlProvider>;
type Messages = IntlProviderProps['messages'];

export const PdpRespMarketplaceLayout: FC<{
    store: Store;
    locale: Locale;
    messages: Messages;
    primaryRelayVariables: PrimaryRelayVariables;
    secondaryRelayVariables: SecondaryRelayVariables;
    serverSecondaryQueryRef?: PreloadedQuery<pdpRespMarketplaceSecondaryRootQueryType> | null;
}> = ({
    locale,
    messages,
    primaryRelayVariables,
    secondaryRelayVariables,
    store,
    serverSecondaryQueryRef,
}) => {
    const primaryData = useLazyLoadQuery<PdpRespMarketplacePrimaryRootQueryType>(
        pdpRespMarketplacePrimaryRootQuery,
        primaryRelayVariables,
        { fetchPolicy: 'store-only' }
    );

    useDwelledOnPage(PAGE_TYPE.PDP);

    // test tracking
    useEffect(() => {
        trackItemFavoritingFeb2026Test();
    }, []);

    return (
        <IntlProvider locale={locale} messages={messages}>
            <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                <Provider store={store}>
                    <PdpRespMarketplaceLayoutPrimary viewer={primaryData.viewer} />
                </Provider>
                <DblExternalContextProvider store={store}>
                    <PdpRespMarketplaceLayoutSecondaryRenderer
                        relayVariables={secondaryRelayVariables}
                        serverSecondaryQueryRef={serverSecondaryQueryRef}
                    />
                </DblExternalContextProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
};
